import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import AddressEditor from '@/components/entities/AddressEditor';
import PersonPicker from '@/components/entities/PersonPicker';
import OrgPicker from '@/components/entities/OrgPicker';
import api from '@/lib/api';
import { toast } from 'sonner';
import { setCreatedClientId } from '@/lib/formDraft';
import { UserPlus, ArrowLeft } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const TYPES = ['individual', 'organization'];
const STATUSES = ['active', 'prospect', 'inactive'];

const emptyForm = () => ({
  type: 'individual', status: 'active', person_id: null,
  organization_id: null, representative_id: null, addresses: [], notes: '',
});

const toForm = (c) => ({
  type: c.type || 'individual', status: c.status || 'active',
  person_id: c.person_id || null, organization_id: c.organization_id || null,
  representative_id: c.representative_id || null,
  addresses: (c.addresses || []).map((a) => ({ ...a })), notes: c.notes || '',
});

/**
 * Full-page create/edit for a Client (a role over a Person or Organization).
 * Client creation is always a page (never a modal) so the Person/Org pickers it
 * hosts open at most one modal deep. Supports a ?returnTo hand-off so a Case /
 * Appointment page can create a client inline and get it back selected.
 */
const ClientForm = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo');
  const editing = !!id;

  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(!editing);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/clients/${id}`);
      setForm(toForm(res.data));
    } catch {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setReady(true);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }
    if (user && !['admin', 'senior_partner', 'associate'].includes(user.role)) {
      toast.error('Access denied. Staff privileges required.');
      navigate('/');
      return;
    }
    if (user && editing) load();
  }, [user, loading, navigate, editing, load]);

  const principalChosen = form.type === 'individual' ? form.person_id : form.organization_id;

  const goBack = () => navigate(returnTo || '/clients');

  const save = async () => {
    setSubmitting(true);
    try {
      const clientId = id || uuidv4();
      const payload = {
        id: clientId,
        type: form.type,
        status: form.status,
        person_id: form.type === 'individual' ? form.person_id : null,
        organization_id: form.type === 'organization' ? form.organization_id : null,
        representative_id: form.representative_id || null,
        addresses: form.addresses,
        notes: form.notes.trim() || null,
        metadata: {},
      };
      if (editing) {
        await api.put(`/clients/${clientId}`, payload);
        toast.success('Client updated');
      } else {
        await api.post('/clients', payload);
        toast.success('Client created');
      }
      // If we were opened to fill a client field on another form, hand the new
      // client's id back and return there; otherwise go to the directory.
      if (returnTo && !editing) setCreatedClientId(clientId);
      goBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save client');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || !ready) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title={editing ? 'Edit Client' : 'New Client'}
        icon={UserPlus}
        titleTestId="client-form-title"
        actions={
          <Button variant="outline" onClick={goBack} data-testid="client-form-back">
            <ArrowLeft size={18} /> {returnTo ? 'Cancel' : 'Back to clients'}
          </Button>
        }
      />

      <Card className="max-w-2xl">
        <CardContent className="p-6 space-y-4" data-testid="client-form">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cl-type">Type</Label>
            <select
              id="cl-type" data-testid="cl-type"
              className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              value={form.type}
              disabled={editing}
              onChange={(e) => setForm({ ...form, type: e.target.value, person_id: null, organization_id: null })}
            >
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="cl-status">Status</Label>
            <select id="cl-status" data-testid="cl-status" className="w-full border rounded-md px-3 py-2 text-sm bg-background" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {form.type === 'individual' ? (
          <div>
            <Label>Person *</Label>
            <PersonPicker value={form.person_id} onChange={(v) => setForm({ ...form, person_id: v })} testId="cl-person" />
            <p className="text-xs text-muted-foreground mt-1">The individual this client represents. Name &amp; email are derived from this person.</p>
          </div>
        ) : (
          <div>
            <Label>Organization *</Label>
            <OrgPicker value={form.organization_id} onChange={(v) => setForm({ ...form, organization_id: v })} testId="cl-org" />
            <p className="text-xs text-muted-foreground mt-1">Contact details are inherited from the organization&apos;s primary contact.</p>
          </div>
        )}

        <div>
          <Label>Representative (optional)</Label>
          <PersonPicker value={form.representative_id} onChange={(v) => setForm({ ...form, representative_id: v })} testId="cl-representative" placeholder="No representative" />
          <p className="text-xs text-muted-foreground mt-1">If set, the representative is the billing correspondent.</p>
        </div>

        <AddressEditor value={form.addresses} onChange={(addresses) => setForm({ ...form, addresses })} label="Engagement addresses" />

        <div>
          <Label htmlFor="cl-notes">Notes</Label>
          <Textarea id="cl-notes" data-testid="cl-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="flex gap-4 pt-2">
          <Button variant="outline" onClick={goBack} className="flex-1">Cancel</Button>
          <Button onClick={save} className="flex-1" disabled={!principalChosen || submitting} data-testid="save-client-btn">
            {editing ? 'Update Client' : 'Create Client'}
          </Button>
        </div>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default ClientForm;
